// Shared helpers for every datetime-local field in the app (the wizard's
// Travel Details step, the advance-request form) - kept in one place so
// the timezone handling below can't drift between them.

// datetime-local inputs give "2026-10-01T18:00" with no timezone - this is
// deliberately sent to the backend AS-IS (just padded to full ISO with
// ":00" seconds), never converted to UTC via toISOString(). A trip's "day"
// is the traveler's own wall-clock day wherever they are, not a UTC day:
// converting to UTC used to shift day-boundary math by the browser's UTC
// offset, silently changing full/half-day counts (and therefore the
// allowance amount) depending on what timezone happened to be open -
// identical local-time trips landed on different day counts depending on
// the browser. Sending the naive local string keeps day-boundary math
// anchored to what the traveler actually typed (see db/travel_duration.py).
export function toIso(datetimeLocal: string): string {
  return datetimeLocal.length === 16 ? `${datetimeLocal}:00` : datetimeLocal;
}

// A plain "YYYY-MM-DD" (a bill date, an advance's planned date) is read as
// that LOCAL day - new Date("2026-11-03") alone means UTC midnight, which
// shows as 2 November anywhere west of UTC.
function toDate(value: string | Date): Date {
  if (value instanceof Date) return value;
  return new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value);
}

/** Every date the app shows, as day/month/year (3/11/2026) on every device.
 *  Never use toLocaleDateString() for display - it follows the device's
 *  region, so a US-region phone showed the same date as 11/3/2026. */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "-";
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return "-";
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
}

/** formatDate plus a 24-hour time (3/11/2026 14:05) - the same shape the
 *  date-time picker shows. */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "-";
  const d = toDate(value);
  if (Number.isNaN(d.getTime())) return "-";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${formatDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Inverse of toIso - reads back a naive local datetime string (as stored,
// or as returned by the backend) into the "YYYY-MM-DDTHH:mm" shape a
// datetime-local input needs. No timezone conversion, matching toIso.
export function toLocalDatetimeInput(isoOrLocal: string | null | undefined): string {
  // Null on an Other reimbursement request/advance, which has no trip dates.
  return (isoOrLocal ?? "").slice(0, 16);
}
